import uniq from 'lodash/fp/uniq'

import {
  Phase,
  PhaseDiscount,
  PhaseMinimum,
  Price
} from '../atoms/reviewPricing.primitives.atoms'

export type PriceWithDiscount = Price & {
  discount?: PhaseDiscount
  boundingBoxIndex: number
}

export const getPricesForPhase = ({
  phase,
  prices
}: {
  phase: Phase
  prices: Array<Price>
}): Array<Price> => {
  return phase.priceIds
    .map(priceId => prices.find(p => p.id === priceId))
    .filter((p): p is Price => Boolean(p))
}

export const getDiscountScopeAvailability = ({
  phase,
  prices,
  discountId
}: {
  phase: Phase
  prices: Price[]
  discountId?: string
}) => {
  const otherDiscounts = phase.discounts.filter(
    discount => discount.id !== discountId
  )
  const pricesForPhase = getPricesForPhase({ phase, prices })
  const availablePriceIds = pricesForPhase
    .map(({ id }) => id)
    .filter(priceId =>
      otherDiscounts.every(
        discount => !discount.restrictToPrices.includes(priceId)
      )
    )
  const recurringFrequencies = uniq(
    pricesForPhase
      .map(price =>
        price.billingFrequency !== 'ONE_TIME'
          ? price.billingFrequency
          : undefined
      )
      .filter(Boolean)
  )
  const hasCompatibleRecurringFrequencies = recurringFrequencies.length < 2
  const hasAnotherGlobalDiscount = otherDiscounts.some(
    discount => discount.restrictToPrices.length === 0
  )

  return {
    availablePriceIds,
    isApplyToAllAvailable:
      hasCompatibleRecurringFrequencies && !hasAnotherGlobalDiscount
  }
}

/**
 * Replaces a single phase in the pricing details, leaving every other phase
 * untouched. Pricing edits only ever apply to the phase being edited.
 */
export const updatePhase = ({
  phases,
  phaseId,
  update
}: {
  phases: Array<Phase>
  phaseId: string
  update: (phase: Phase) => Phase
}): Array<Phase> =>
  phases.map(phase => (phase.id === phaseId ? update(phase) : phase))

export const enhancePricesWithDiscounts = ({
  phase,
  prices
}: {
  phase: Phase
  prices: Array<Price>
}): Array<PriceWithDiscount> => {
  return phase.priceIds
    .map(priceId => prices.find(p => p.id === priceId))
    .filter((p): p is Price => Boolean(p))
    .map(price => ({
      ...price,
      boundingBoxIndex: prices.findIndex(p => p.id === price.id),
      discount: phase.discounts.find(d => d.restrictToPrices.includes(price.id))
    }))
}

export const groupPricesByMinimum = ({
  phase,
  prices
}: {
  phase: Phase
  prices: Array<PriceWithDiscount>
}): Array<{
  id: string
  minimum?: PhaseMinimum & {
    billingFrequency: Price['billingFrequency']
  }
  boundingBoxIndex: number
  prices: Array<PriceWithDiscount>
}> => {
  const hasGlobalMinimum = phase.minimums.find(
    minimum => minimum.restrictToPrices.length === 0
  )

  const pricesWithNoMinimum = hasGlobalMinimum
    ? prices.filter(p => !('usageMetricId' in p.structure))
    : prices.filter(
        p => !phase.minimums.find(m => m.restrictToPrices.includes(p.id))
      )

  return [
    {
      id: `${phase.id}.no-minimum`,
      prices: pricesWithNoMinimum,
      boundingBoxIndex: -1
    },
    ...phase.minimums.map((minimum, idx) => {
      const isGlobalMinimum = minimum.restrictToPrices.length === 0

      const pricesInGroup = isGlobalMinimum
        ? prices.filter(price => 'usageMetricId' in price.structure)
        : prices.filter(price => minimum.restrictToPrices.includes(price.id))

      const priceFrequency = pricesInGroup[0]?.billingFrequency ?? 'MONTHLY'

      return {
        id: `${phase.id}.minimum.${idx}`,
        boundingBoxIndex: idx,
        minimum: {
          ...minimum,
          billingFrequency: priceFrequency
        },
        prices: pricesInGroup
      }
    })
  ]
}

export const getGlobalDiscount = ({
  phase
}: {
  phase: Phase
}): PhaseDiscount | undefined => {
  const globalDiscount = phase.discounts.find(
    d => d.restrictToPrices.length === 0
  )
  return globalDiscount
}
